import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Calendar, Users, Sparkles, Zap, Clock, DollarSign, ArrowRight, Package, Activity, Shield, FileText, Code, Key, Fingerprint, Lock, TrendingUp, Star, Link2, QrCode, Camera, Bell, Download, Mail, Menu, X, Globe, Bitcoin, BadgeDollarSign, Percent, SlidersHorizontal, BarChart3, Anchor, MapPin, Database, ArrowLeftRight, Megaphone, MousePointerClick } from 'lucide-react';
import { BeaconIcon } from '../components/BeaconIcon';
import { QRCodeSVG as QRCodeComponent } from 'qrcode.react';
import { getAllPlans, formatPrice } from '../config/pricing';
import { SocialLinksDark } from '../components/SocialLinks';
import HelpScoutBeacon from '../components/HelpScoutBeacon';
import { proxyImg } from '../utils/img';

// Auto-rotating top announcement banner slides (module scope = stable identity).
const ANNOUNCEMENTS = [
  {
    href: '/customers/boatx',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'BOATX processed $100k+ in bookings',
    rest: '\u00A0through BookingFlows',
    cta: '— see how',
  },
  {
    href: '/customers/banana-boat-rentals',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'Welcoming Banana Boat Rentals',
    rest: '\u00A0— 15+ boat fleet now managed on BookingFlows',
    cta: 'read their story',
  },
  {
    href: '/meet-the-team',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'USCG · RYA · MCA · IYT · ASA',
    rest: '\u00A0— built by certified captains',
    cta: 'meet the team',
  },
];

const SupplierLandingPage = () => {
  const navigate = useNavigate();
  const plans = getAllPlans(false); // Use demo pricing
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Top announcement banner — slides up after the user scrolls past the hero
  const [bannerVisible, setBannerVisible] = useState(true);
  // Floating "Start free" CTA — only appears once the user is past the hero,
  // so it doesn't compete with the in-hero buttons. Pure conversion nudge for
  // visitors who scroll deep without ever returning to the nav.
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  // Typed rotator for the gradient hero buzz-line. Cycles through a small set
  // of operator-resonant outcomes (Less busywork → More time for what matters
  // → Happier customers → …). Pure CSS caret + JS typing/erasing — no deps.
  // Honors prefers-reduced-motion: users who opt out see the first phrase
  // rendered statically with no animation.
  const BUZZ_LINES = [
    'Less busywork.',
    'More time.',
    'Happy customers.',
    'Higher margins.',
    'Fewer no-shows.',
  ];
  const [buzzIdx, setBuzzIdx] = useState(0);
  const [buzzText, setBuzzText] = useState(BUZZ_LINES[0]);
  const [buzzPhase, setBuzzPhase] = useState('hold'); // hold → erase → type → hold
  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined' &&
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return; // Leave the static first phrase visible.

    const fullText = BUZZ_LINES[buzzIdx];
    let timer;
    if (buzzPhase === 'hold') {
      timer = setTimeout(() => setBuzzPhase('erase'), 1800);
    } else if (buzzPhase === 'erase') {
      if (buzzText.length === 0) {
        const next = (buzzIdx + 1) % BUZZ_LINES.length;
        setBuzzIdx(next);
        setBuzzPhase('type');
      } else {
        timer = setTimeout(() => setBuzzText(buzzText.slice(0, -1)), 35);
      }
    } else if (buzzPhase === 'type') {
      if (buzzText.length === fullText.length) {
        setBuzzPhase('hold');
      } else {
        timer = setTimeout(() => setBuzzText(fullText.slice(0, buzzText.length + 1)), 65);
      }
    }
    return () => clearTimeout(timer);
  }, [buzzPhase, buzzText, buzzIdx]);

  // Auto-rotating announcement banner index (crossfade). Pauses on hover;
  // honors prefers-reduced-motion (no auto-advance).
  const [announceIdx, setAnnounceIdx] = useState(0);
  const [announcePaused, setAnnouncePaused] = useState(false);
  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined' &&
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || announcePaused) return;
    const id = setInterval(() => {
      setAnnounceIdx((i) => (i + 1) % ANNOUNCEMENTS.length);
    }, 6000);
    return () => clearInterval(id);
  }, [announcePaused]);

  // Lightweight wrapper around analytics util (kept inside component so the
  // import isn't loaded on first paint).
  const trackSignupStarted = (source) => {
    import('../utils/analytics').then((m) => {
      if (typeof m.trackSignupStarted === 'function') {
        m.trackSignupStarted(source);
      }
    }).catch(() => {});
  };

  // Add scroll animation observer + banner auto-hide on scroll
  useEffect(() => {
    // Fire supplier page view — B2B lead signal for Google Ads
    import('../utils/analytics').then(({ trackSupplierPageView }) => {
      trackSupplierPageView('/for-suppliers');
    });

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    // Observe all sections with the class 'animate-on-scroll'
    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    // Hide top banner once user has scrolled past ~120px (past hero start).
    // Show the floating "Start free" CTA once the user is past ~700px (deep
    // enough that they've engaged with the page but no longer see the
    // primary hero CTAs).
    const onScroll = () => {
      const y = window.scrollY;
      setBannerVisible(y < 120);
      setShowFloatingCta(y > 700);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HelpScoutBeacon />
      <Helmet>
        <title>Online Booking Software for Tours, Charters &amp; Rentals | BookingFlows</title>
        <meta name="description" content="BookingFlows is online booking software for tours, charters, rentals &amp; experience businesses. Take bookings 24/7, get paid automatically, manage your team. Sign up free." />
        <meta property="og:title" content="Online Booking Software | BookingFlows" />
        <meta property="og:description" content="Take bookings 24/7, get paid automatically, manage your team. Free to start." />
        {/* Canonical points to the root — / and /for-suppliers serve the same
            page so this avoids duplicate-content penalties while keeping the
            /for-suppliers route alive for existing Google Ads campaigns. */}
        <link rel="canonical" href="https://bookingflows.com/" />
      </Helmet>
      <style>{`
        /* Smooth scroll animations - subtle and professional */
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        
        .animate-fade-in-up {
          opacity: 1;
          transform: translateY(0);
        }

        /* Smooth scroll behavior */
        html {
          scroll-behavior: smooth;
        }
        
        /* Enhanced button hover states */
        .btn-supplier-primary {
          transition: all 0.2s ease;
        }
        .btn-supplier-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
        }
        .btn-supplier-primary:active {
          transform: translateY(0);
        }
      `}</style>
      
      {/* Top announcement banner — auto-rotating crossfade (BOATX blue ⇄ Banana
          Boat Rentals yellow ⇄ credential ribbon). Fixed height (h-11) so every
          slide stays vertically centered on all screens. Pauses on hover;
          auto-hides on scroll past the hero. */}
      <div
        data-testid="supplier-top-banner"
        className={`relative overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          bannerVisible ? 'h-11 max-h-12 opacity-100' : 'h-11 max-h-0 opacity-0'
        }`}
        onMouseEnter={() => setAnnouncePaused(true)}
        onMouseLeave={() => setAnnouncePaused(false)}
      >
        {ANNOUNCEMENTS.map((a, i) => (
          <a
            key={i}
            href={a.href}
            data-testid={`top-banner-slide-${i}`}
            aria-hidden={i !== announceIdx}
            className={`absolute inset-0 flex items-center justify-center text-center text-xs sm:text-sm font-medium tracking-wide leading-none transition-opacity duration-700 ease-out ${a.bg} ${a.text} ${
              i === announceIdx ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 flex items-center justify-center gap-1.5 sm:gap-2">
              <span>
                <span className="font-semibold">{a.bold}</span>
                <span className="hidden sm:inline">{a.rest}</span>
              </span>
              <span className="opacity-90 underline-offset-2 hover:underline whitespace-nowrap inline-flex items-center gap-1 shrink-0">
                {a.cta}
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </a>
        ))}
      </div>

      {/* Header/Nav */}
      <header className="border-b border-gray-100 sticky top-0 z-50 bg-white/90 backdrop-blur-md shadow-sm shadow-gray-200/40 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="w-10 h-10 rounded-xl object-cover"
              />
              <span className="text-2xl font-bold text-gray-900">BookingFlows</span>
            </div>
            
            {/* Desktop Buttons - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => navigate('/marketplace')}
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors px-3 py-2 rounded-full"
                data-testid="supplier-nav-explore"
              >
                Explore experiences <span aria-hidden="true">→</span>
              </button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-full"
              >
                Log In
              </Button>
              <Button 
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium px-5 py-2.5 rounded-full shadow-sm"
                data-testid="supplier-nav-signup"
              >
                Sign Up Free
              </Button>
            </div>
            
            {/* Mobile Menu Button - Visible on mobile only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>
          
          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-2 border-t border-gray-100 pt-4">
              <Button 
                variant="ghost" 
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                className="w-full btn-landing-ghost justify-start"
              >
                Log In
              </Button>
              <Button 
                onClick={() => {
                  navigate('/signup');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2.5 rounded-full"
              >
                Sign Up Free
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section — refined to match marketplace's airy, minimal aesthetic */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 lg:pt-28 lg:pb-32">
        {/* Soft gradient blobs — give the page depth without feeling heavy.
            Mirrors the marketplace hero's "from-blue-50 via-white to-indigo-50"
            ambient feel. */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/60 via-white to-indigo-50/40 -z-10" aria-hidden="true" />
        <div className="absolute top-10 -left-20 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl -z-10 pointer-events-none" aria-hidden="true" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-indigo-200/30 rounded-full blur-3xl -z-10 pointer-events-none" aria-hidden="true" />

        <div className="text-center max-w-4xl mx-auto animate-on-scroll">
          {/* Eyebrow — subtle, not a heavy filled pill */}
          <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Booking Software for Operators
          </div>

          {/* Headline — concise elevator pitch. Mirrors marketplace pattern:
              dark first line, gradient second line. */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.05] mb-5">
            More bookings.<br />
            <span
              className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 inline-block leading-[1.18] pb-1 whitespace-nowrap"
              aria-live="polite"
              aria-atomic="true"
              data-testid="supplier-hero-rotator"
            >
              {buzzText || '\u00A0'}
            </span>
            <span
              className="inline-block w-[0.08em] h-[0.85em] align-[-0.08em] ml-1 bg-blue-400 animate-pulse"
              aria-hidden="true"
            />
          </h1>

          <p className="text-base sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto mb-9 px-2">
            The all-in-one booking platform built by tour, charter &amp; rental operators —
            for operators. Take bookings 24/7, get paid instantly, run your team.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => { trackSignupStarted('supplier_hero'); navigate('/signup'); }}
              size={null}
              className="btn-supplier-primary bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium text-base px-8 py-3.5 rounded-full shadow-lg shadow-blue-500/20"
              data-testid="supplier-hero-signup"
            >
              Start free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size={null}
              onClick={() => navigate('/meet-the-team')}
              className="text-base px-7 py-3.5 rounded-full border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700"
              data-testid="supplier-hero-meet-team"
            >
              Meet the team
            </Button>
          </div>

          <p className="text-xs text-gray-400 mt-5 tracking-wide">
            Free setup · No credit card required · Live in 15 minutes
          </p>
        </div>
      </section>

      {/* Trusted-by strip — clean, no inflated stats. Just authentic logos. */}
      <section className="border-y border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-semibold">
              Powered by best-in-class infrastructure
            </p>
            <div className="flex items-center gap-8 sm:gap-10 flex-wrap justify-center">
              {/* Google */}
              <div className="flex items-center gap-2" title="Google Business Profile">
                <img
                  src="/images/integrations/google-g.png"
                  alt="Google"
                  className="h-6 w-6"
                  loading="lazy"
                />
                <span className="text-sm font-medium text-gray-600">Google</span>
              </div>
              {/* Stripe */}
              <div className="flex items-center" title="Stripe — payments">
                <svg className="w-12 h-5" viewBox="0 0 60 25" fill="none" aria-hidden="true">
                  <path fillRule="evenodd" clipRule="evenodd" d="M60 12.8c0-4.28-2.07-7.66-6.03-7.66-3.98 0-6.38 3.38-6.38 7.63 0 5.04 2.84 7.58 6.92 7.58 1.99 0 3.49-.45 4.63-1.09v-3.33c-1.14.57-2.45.93-4.11.93-1.63 0-3.07-.57-3.25-2.56h8.19c0-.22.03-1.08.03-1.5zm-8.19-1.59c0-1.88 1.15-2.67 2.19-2.67 1.02 0 2.1.79 2.1 2.67h-4.29zM41.3 5.14c-1.64 0-2.7.77-3.28 1.3l-.22-1.04H34v19.8l4.22-.9v-4.8c.6.44 1.49 1.06 2.97 1.06 3 0 5.74-2.42 5.74-7.77 0-4.9-2.78-7.65-5.63-7.65zm-.99 11.76c-.99 0-1.57-.36-1.98-.8v-6.3c.44-.48 1.04-.82 1.98-.82 1.52 0 2.57 1.7 2.57 3.94 0 2.3-1.03 3.98-2.57 3.98zM27.65 4.3l4.24-.9V0l-4.24.9v3.4zm0 .98h4.24v14.6h-4.24V5.28zM22.94 6.6l-.26-1.32h-3.63v14.6h4.22v-9.9c1-.3 2.68-1.06 3.2-.88V5.28c-.54-.2-2.51-.57-3.53 1.32zM13.68 1.38L9.56 2.25v13.35c0 2.47 1.85 4.28 4.3 4.28 1.36 0 2.35-.25 2.9-.55v-3.45c-.53.22-3.17 1-3.17-1.5V8.93h3.17V5.28h-3.17l.09-3.9zM4.16 9.84c0-.65.54-.9 1.42-.9 1.27 0 2.87.39 4.14 1.08V6.2c-1.39-.55-2.76-.77-4.14-.77C2.25 5.43 0 7.1 0 10.02c0 4.5 6.2 3.78 6.2 5.72 0 .77-.67 1.02-1.6 1.02-1.39 0-3.17-.57-4.58-1.35v3.82c1.56.67 3.14.96 4.58.96 3.35 0 5.58-1.66 5.58-4.72 0-4.86-6.22-4-6.22-5.63z" fill="#635BFF"/>
                </svg>
              </div>
              {/* Render — infrastructure host */}
              <div className="flex items-center" title="Render — hosted infrastructure">
                <img
                  src="/images/integrations/render-icon.png"
                  alt="Render"
                  className="h-7 w-auto"
                  loading="lazy"
                />
              </div>
              {/* AWS — encrypted daily data backups */}
              <div className="flex items-center" title="Amazon Web Services — encrypted daily data backups">
                <img
                  src="/images/integrations/aws-logo.svg"
                  alt="AWS"
                  className="h-5 w-auto"
                  loading="lazy"
                  data-testid="trusted-infra-aws"
                />
              </div>
              {/* Cloudflare — security & global CDN */}
              <div className="flex items-center" title="Cloudflare — security & global CDN">
                <img
                  src="/images/integrations/cloudflare-logo.png"
                  alt="Cloudflare"
                  className="h-[18px] w-auto"
                  loading="lazy"
                />
              </div>
              {/* Twilio — SMS & WhatsApp notifications */}
              <div className="flex items-center gap-2" title="Twilio — SMS & WhatsApp notifications">
                <img
                  src="/images/integrations/twilio-icon.svg"
                  alt="Twilio"
                  className="h-5 w-5"
                  loading="lazy"
                  data-testid="trusted-infra-twilio"
                />
                <span className="text-sm font-medium text-gray-600">Twilio</span>
              </div>
              {/* WhatsApp — customer messaging */}
              <div className="flex items-center gap-2" title="WhatsApp — customer messaging">
                <img
                  src="/images/integrations/whatsapp-logo.webp"
                  alt="WhatsApp"
                  className="h-6 w-6 rounded-md"
                  loading="lazy"
                  data-testid="trusted-infra-whatsapp"
                />
                <span className="text-sm font-medium text-gray-600">WhatsApp</span>
              </div>
              {/* GetYourGuide removed — distribution partner, not infrastructure */}
            </div>
          </div>
        </div>
      </section>

      {/* Customer Story bar removed — case study lives at /customers/boatx
          and is reachable via the "See the BOATX story" hero CTA. Keeps
          the homepage breathing into the feature grid below. */}



      {/* ───────────────────────────────────────────────────────────────
          Banana Boat Rentals spotlight — a dedicated highlight for our
          newest operator (Austin's largest party-boat fleet). Placed high
          on the page; BOATX is represented in the top announcement banner.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white" data-testid="supplier-bbr-spotlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-neutral-950 via-neutral-900 to-black text-white shadow-[0_40px_90px_-35px_rgba(0,0,0,0.65)]">
            {/* dot texture + yellow glow */}
            <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
                 style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,221,51,0.5) 1px, transparent 0)", backgroundSize: "30px 30px" }} />
            <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-yellow-400/15 blur-3xl pointer-events-none" />

            <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center p-7 sm:p-10 lg:p-14">
              {/* Left — copy */}
              <div>
                <div className="flex justify-center lg:justify-start mb-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400/15 border border-yellow-300/25 text-[11px] font-semibold uppercase tracking-widest text-yellow-200">
                    <Star className="w-3.5 h-3.5 fill-yellow-300 text-yellow-300" /> Newest on BookingFlows
                  </div>
                </div>

                <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] mb-4">
                  Welcoming <span className="bg-gradient-to-r from-yellow-300 to-amber-400 bg-clip-text text-transparent">Banana Boat Rentals</span> — Austin's largest party-boat fleet.
                </h2>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-7 max-w-xl">
                  We're proud to power Austin's biggest fleet. 15+ boats across Lake Austin
                  and Lake Travis now run on BookingFlows — paired with a clean, modern
                  website and local SEO we built from the ground up. Thank you for trusting
                  us with the operation.
                </p>

                <div className="flex flex-wrap gap-2.5 mb-8">
                  {[
                    { icon: Anchor, label: '15+ party boats' },
                    { icon: Star,   label: '500+ 5-star reviews' },
                    { icon: MapPin, label: 'Lake Austin & Travis' },
                  ].map(({ icon: Icon, label }) => (
                    <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-sm text-gray-200">
                      <Icon className="w-3.5 h-3.5 text-yellow-300" /> {label}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate('/customers/banana-boat-rentals')}
                    data-testid="bbr-spotlight-story-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-yellow-400 text-black font-semibold hover:bg-yellow-300 transition-colors"
                  >
                    Read their story <ArrowRight className="w-4 h-4" />
                  </button>
                  <a
                    href="https://www.bananaboatrentals.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="bbr-spotlight-visit-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white font-medium transition-colors backdrop-blur"
                  >
                    Visit bananaboatrentals.com <Globe className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Right — floating collage */}
              <div className="relative">
                <div className="grid grid-cols-2 grid-rows-3 gap-3 aspect-[4/5]">
                  <img
                    src={proxyImg("https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_hero_bridge.jpg", 800, 80)}
                    alt="Banana Boat Rentals guests with the Pennybacker Bridge on Lake Austin"
                    className="col-span-2 row-span-2 w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                    loading="lazy"
                    decoding="async"
                  />
                  <img
                    src={proxyImg("https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_party_group.jpg", 500, 80)}
                    alt="A large group celebrating on a Banana Boat Rentals party boat"
                    className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                    loading="lazy"
                    decoding="async"
                  />
                  <img
                    src={proxyImg("https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_yellow_pontoon.jpg", 500, 80)}
                    alt="A bright yellow Banana Boat Rentals pontoon cruising Lake Austin"
                    className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                {/* floating Banana Boat Rentals logo — top-left */}
                <div className="absolute -top-3 -left-2 sm:-left-4 z-10 bg-black rounded-xl ring-1 ring-white/15 px-2.5 py-1.5 shadow-2xl">
                  <img src="https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_logo.png"
                       alt="Banana Boat Rentals" className="h-7 sm:h-9 w-auto object-contain" loading="lazy" />
                </div>
                {/* floating fleet pill */}
                <div className="absolute -top-3 -right-2 sm:-right-4 bg-yellow-400 text-black rounded-xl px-3.5 py-2 font-bold text-sm shadow-2xl flex items-center gap-1.5">
                  <Anchor className="w-4 h-4" /> 15+ boats
                </div>
                {/* floating review pill */}
                <div className="absolute -bottom-4 -left-2 sm:-left-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                  <div className="flex">
                    {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                  </div>
                  <div className="text-xs leading-tight">
                    <div className="font-semibold">500+ Google reviews</div>
                    <div className="text-gray-500">Austin's largest fleet</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────────────────────────────────────────────
          HELM Intelligence by BookingFlows — flagship analytics suite.
          Placed top-of-page per founder: market the outcome, not the
          methodology. All numbers below are illustrative.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-[#EFF5FF] to-white" data-testid="supplier-helm-section">
        {/* Floaty gradient backdrop — matches the HELM suite, faded at section edges */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-16 -left-32 w-[520px] h-[520px] bg-blue-200/30 rounded-full blur-3xl" />
          <div className="absolute bottom-16 -right-32 w-[480px] h-[480px] bg-indigo-200/25 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EBF2FF] border border-[#2563EB]/15 mb-6">
                <img src="/bf-intelligence-logo.png" alt="HELM Intelligence" className="w-4 h-4 object-contain" />
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#2563EB]">New · Included free with BookingFlows</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-gray-900 mb-2">
                Meet HELM <span className="italic">Intelligence</span>.
              </h2>
              <p className="text-sm font-semibold text-gray-400 tracking-wide mb-5">by BookingFlows</p>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-xl">
                Your booking data, decoded. HELM reads every trip you run and turns it
                into clear moves — what each boat really earns, which services sell,
                when demand peaks, and who keeps coming back.
              </p>

              <div className="space-y-3.5 mb-8">
                {[
                  { icon: Anchor, title: 'Asset Earnings', desc: 'Revenue, rev/hour and seats filled — per boat.' },
                  { icon: TrendingUp, title: 'Revenue Mix', desc: 'What actually sells, realized prices and add-on attach.' },
                  { icon: Calendar, title: 'Demand Center', desc: 'Peak days, start times and how far ahead customers book.' },
                  { icon: Users, title: 'Customer Insights', desc: 'Repeat rates, group sizes and your highest-value guests.' },
                ].map((f) => (
                  <div key={f.title} className="flex items-start gap-3.5">
                    <span className="w-9 h-9 rounded-full bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center shrink-0">
                      <f.icon className="w-4 h-4" strokeWidth={1.75} />
                    </span>
                    <div>
                      <span className="font-semibold text-gray-900 text-sm">{f.title}</span>
                      <span className="text-gray-500 text-sm"> — {f.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-3 rounded-2xl bg-white ring-1 ring-slate-900/[0.05] shadow-sm p-4 mb-8 max-w-xl">
                <Sparkles className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" />
                <p className="text-sm text-gray-600 leading-relaxed">
                  <span className="font-semibold text-gray-900">AI Suggestions built in.</span>{' '}
                  HELM spots the gaps and hands you one clear move — read-only, from your data only.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Button
                  size="lg"
                  onClick={() => { trackSignupStarted('supplier_helm_section'); navigate('/signup'); }}
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-7"
                  data-testid="helm-section-cta"
                >
                  Start free — HELM included <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <span className="text-xs text-gray-400 font-medium">Your data only · No setup · Every plan</span>
              </div>
            </div>

            {/* Right — suite mock (illustrative numbers) */}
            <div className="relative" aria-hidden="true">
              <div className="absolute -top-5 left-6 z-10 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white shadow-lg ring-1 ring-slate-900/[0.06]">
                <img src="/bf-intelligence-logo.png" alt="" className="w-5 h-5 object-contain" />
                <span className="text-xs font-bold text-slate-900">HELM <span className="italic">Intelligence</span></span>
              </div>

              <div className="rounded-[1.75rem] bg-white/80 backdrop-blur-sm ring-1 ring-slate-900/[0.05] shadow-[0_40px_80px_-30px_rgba(37,99,235,0.35)] p-5 sm:p-7">
                {/* KPI row */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="rounded-2xl bg-white ring-1 ring-slate-900/[0.04] shadow-sm p-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5">Fleet revenue</div>
                    <div className="text-xl sm:text-2xl font-extrabold text-[#2563EB]" style={{ fontVariantNumeric: 'tabular-nums' }}>$128,410</div>
                    <div className="text-[11px] text-slate-400 mt-1">last 12 months</div>
                  </div>
                  <div className="rounded-2xl bg-white ring-1 ring-slate-900/[0.04] shadow-sm p-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5">Busiest day</div>
                    <div className="text-xl sm:text-2xl font-extrabold text-[#2563EB]">Saturday</div>
                    <div className="text-[11px] text-slate-400 mt-1">41% of all trips</div>
                  </div>
                </div>

                {/* Mini chart */}
                <div className="rounded-2xl bg-white ring-1 ring-slate-900/[0.04] shadow-sm p-4 mb-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Monthly fleet revenue</div>
                  <div className="flex items-end gap-1.5 h-24">
                    {[28, 34, 30, 46, 58, 72, 88, 100, 84, 62, 44, 52].map((h, i) => (
                      <div
                        key={i}
                        className={`flex-1 rounded-t-md ${i === 7 ? 'bg-[#2563EB]' : 'bg-[#BFDBFE]'}`}
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                </div>

                {/* AI suggestion card */}
                <div className="rounded-2xl bg-[#EBF2FF]/70 border border-[#2563EB]/15 p-4">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#2563EB]" />
                      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#2563EB]">AI Suggestion · Midweek gap</span>
                    </div>
                    <span className="text-sm font-extrabold text-[#2563EB]" style={{ fontVariantNumeric: 'tabular-nums' }}>+$7,440/yr</span>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    <span className="font-bold text-[#2563EB]">→ One move:</span>{' '}
                    run lower-priced midweek trips to pull demand off the weekend.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BookingFlows Beacon + Beacon Tender — the pixel that powers HELM's
          acquisition reports and feeds retargeting Campaigns. */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="supplier-beacon-section">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-24 right-0 w-[420px] h-[420px] bg-emerald-100/40 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — copy + snippet + the loop */}
            <div>
              <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-500/20 mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700">BookingFlows Beacon · Your own pixel</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-gray-900 mb-5">
                Your own pixel.<br />Wired straight into your bookings.
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7 max-w-xl">
                Think Google Analytics or Meta Pixel — except every visit flows into
                HELM. One line of code on your website and your traffic becomes
                funnels, audiences and revenue attribution you actually own.
              </p>

              {/* Install snippet */}
              <div className="rounded-2xl bg-slate-900 shadow-[0_24px_48px_-20px_rgba(15,23,42,0.5)] overflow-hidden mb-7 max-w-xl" data-testid="beacon-snippet-block">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <Code className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Install once · anywhere on your site</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                    </span>
                    Live
                  </span>
                </div>
                <pre className="px-4 py-4 text-[12px] sm:text-[13px] leading-relaxed font-mono overflow-x-auto"><code><span className="text-slate-500">&lt;</span><span className="text-sky-300">script</span> <span className="text-emerald-300">defer</span> <span className="text-emerald-300">src</span><span className="text-slate-500">=</span><span className="text-amber-200">"https://bookingflows.com/beacon.js"</span>{'\n'}  <span className="text-emerald-300">data-bf-org</span><span className="text-slate-500">=</span><span className="text-amber-200">"BF-7F3A-92C1"</span><span className="text-slate-500">&gt;&lt;/</span><span className="text-sky-300">script</span><span className="text-slate-500">&gt;</span></code></pre>
              </div>

              {/* The loop: beacon → intelligence → retarget */}
              <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="beacon-loop-strip">
                {[
                  { icon: BeaconIcon, label: 'Beacon tracks' },
                  { icon: BarChart3, label: 'HELM builds audiences' },
                  { icon: Megaphone, label: 'Campaigns retarget' },
                ].map((s, i) => (
                  <React.Fragment key={s.label}>
                    {i > 0 && <ArrowRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />}
                    <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#EBF2FF] text-[#2563EB] text-xs font-bold">
                      <s.icon className="w-3.5 h-3.5" strokeWidth={2} />
                      {s.label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
              <p className="text-sm text-gray-500 leading-relaxed max-w-xl">
                That's the whole loop — the Beacon watches your traffic, HELM turns it into
                funnels and retargeting audiences, and Campaigns messages them for a few
                dollars a day. No agencies, no ad platforms, no exporting data.
              </p>
            </div>

            {/* Right — Beacon Tender extension mock + live event stream */}
            <div className="relative max-w-md mx-auto lg:mx-0 lg:ml-auto w-full" aria-hidden="true">
              {/* customer visit chip */}
              <div className="absolute -top-6 -left-2 sm:-left-6 z-10 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white shadow-lg ring-1 ring-slate-900/[0.06]">
                <MousePointerClick className="w-3.5 h-3.5 text-[#2563EB]" />
                <span className="text-xs font-bold text-slate-700">A customer lands on your site…</span>
              </div>

              {/* Beacon Tender popup */}
              <div className="rounded-[1.5rem] bg-white ring-1 ring-slate-900/[0.06] shadow-[0_40px_80px_-30px_rgba(16,185,129,0.35)] overflow-hidden" data-testid="beacon-tender-mock">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-xl bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center">
                      <BeaconIcon className="w-5 h-5" />
                    </span>
                    <div>
                      <div className="text-sm font-extrabold text-slate-900 leading-tight">Beacon Tender</div>
                      <div className="text-[10px] font-semibold text-slate-400">by BookingFlows</div>
                    </div>
                  </div>
                <a href="https://chromewebstore.google.com/detail/bookingflows-beacon-tende/lomkgfganpkdkeeeipjdngkcmhbbahkh"
                  target="_blank" rel="noopener noreferrer" data-testid="beacon-tender-store-link"
                  className="px-2.5 py-1 rounded-full bg-emerald-100 text-[9px] font-bold uppercase tracking-wider text-emerald-700 hover:bg-emerald-200 transition-colors">Add to Chrome {'\u2197'}</a>
                </div>

                <div className="px-5 py-5">
                  <div className="text-[11px] font-mono text-slate-400 mb-3">yourcharterco.com</div>
                  <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 border border-emerald-500/20 px-4 py-3.5 mb-4">
                    <span className="relative flex h-3 w-3 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                    </span>
                    <div>
                      <div className="text-sm font-extrabold text-emerald-700">Beacon detected</div>
                      <div className="text-[11px] font-mono text-emerald-600/80">Beacon &hellip;92C1 · firing</div>
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    {['Script loaded on every page', 'Events flowing to HELM', 'Linked to your dashboard'].map((row) => (
                      <div key={row} className="flex items-center gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </span>
                        <span className="text-[13px] font-medium text-slate-600">{row}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Live event stream card */}
              <div className="relative -mt-4 ml-6 sm:ml-10 rounded-2xl bg-white/90 backdrop-blur-sm ring-1 ring-slate-900/[0.06] shadow-xl p-4" data-testid="beacon-event-stream">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2.5">Streaming into HELM</div>
                <div className="space-y-2">
                  {[
                    { ev: 'Page viewed', t: 'just now', d: '0s' },
                    { ev: 'Booking widget viewed', t: '4s ago', d: '0.6s' },
                    { ev: 'Booking activity', t: '11s ago', d: '1.2s' },
                  ].map((r) => (
                    <div key={r.ev} className="flex items-center gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" style={{ animationDelay: r.d }} />
                      <span className="text-[12px] font-semibold text-slate-700">{r.ev}</span>
                      <span className="ml-auto text-[10px] text-slate-400">{r.t}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                  <Megaphone className="w-3.5 h-3.5 text-[#2563EB]" />
                  <span className="text-[11px] font-semibold text-slate-500">Retarget pool: <span className="text-[#2563EB] font-extrabold">+1 visitor</span> ready for Campaigns</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ask Bosun — AI first mate. Character + instant help, guardrails up front. */}
      <section className="py-24 bg-white" data-testid="supplier-bosun-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1F2A5C]/5 border border-[#1F2A5C]/15 mb-6">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#1F2A5C]">⚓ Meet Bosun · Included free</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-gray-900 mb-4">
              Your first mate, always on watch.
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Every BookingFlows dashboard ships with Bosun — a built-in guide who knows the whole vessel.
              Ask where anything lives, how a feature works, or what to set up next, and Bosun points the way.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            {/* Chat mock */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-[0_25px_60px_-25px_rgba(15,23,42,0.25)] overflow-hidden" data-testid="supplier-bosun-chat-mock">
              <div className="bg-[#1F2A5C] px-5 py-4 flex items-center gap-3">
                <img src="/bosun-avatar.png" alt="Bosun — BookingFlows AI first mate" className="w-10 h-10 rounded-full ring-2 ring-[#F5B92E]/60 bg-white/10" />
                <div>
                  <p className="text-white font-bold text-sm leading-tight">Ask Bosun</p>
                  <p className="text-blue-200 text-xs leading-tight">Your first mate for BookingFlows</p>
                </div>
              </div>
              <div className="p-5 space-y-3 bg-gray-50/50">
                <div className="flex justify-end">
                  <div className="bg-[#1F2A5C] text-white text-sm rounded-2xl rounded-br-md px-4 py-2.5 max-w-[80%]">
                    Where do I set up my waiver?
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <img src="/bosun-avatar.png" alt="" className="w-6 h-6 rounded-full" />
                  <div className="bg-white border border-gray-200 text-gray-800 text-sm rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[80%] shadow-sm">
                    Aye — head to Customers → Waivers. Your liability waiver lives there, and it versions automatically every time you edit it.
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-[#1F2A5C] text-white text-sm rounded-2xl rounded-br-md px-4 py-2.5 max-w-[80%]">
                    And my earnings reports?
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <img src="/bosun-avatar.png" alt="" className="w-6 h-6 rounded-full" />
                  <div className="bg-white border border-gray-200 text-gray-800 text-sm rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[80%] shadow-sm">
                    Open the Intelligence Suite and step into the Chartroom — live reports on what earns the most, built from your own bookings.
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-11 h-11 rounded-full bg-[#EBF2FF] text-[#1F2A5C] flex items-center justify-center flex-shrink-0 text-lg">🧭</div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Guided tours, at your pace</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">A Basic tour covers the essentials on day one, and an Advanced tour unlocks your data, audiences and campaigns when you're ready.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-11 h-11 rounded-full bg-[#EBF2FF] text-[#1F2A5C] flex items-center justify-center flex-shrink-0 text-lg">💬</div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Instant answers, day or night</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">No ticket queues for "where do I…" questions. Bosun knows every corner of the platform and answers in seconds.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-11 h-11 rounded-full bg-[#EBF2FF] text-[#1F2A5C] flex items-center justify-center flex-shrink-0 text-lg">🔒</div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Knows the ship — never your secrets</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">Bosun teaches the software only. It has zero access to your customer data, payments or credentials, with strict guardrails and usage limits built in.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built by an operator — founder credentials + global advisory.
          The human, trust-building moment of the page. */}
      <section className="py-24 bg-gradient-to-b from-white via-blue-50/60 to-white relative overflow-hidden">
        <div className="absolute top-24 left-0 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl -z-0" aria-hidden="true" />
        <div className="absolute bottom-24 right-0 w-72 h-72 bg-indigo-200/30 rounded-full blur-3xl -z-0" aria-hidden="true" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Founder image */}
            <div className="order-2 md:order-1">
              <div className="relative max-w-md mx-auto md:mx-0">
                <img
                  src="/images/founder/founder.jpg"
                  alt="BookingFlows founder"
                  className="rounded-3xl shadow-2xl shadow-blue-500/10 w-full object-cover aspect-square"
                />
                {/* Floating "at the helm" accent — real, not stock */}
                <div className="absolute -top-3 -left-2 sm:-top-5 sm:-left-6 w-[4.5rem] sm:w-24 -rotate-[5deg] z-10">
                  <img
                    src={proxyImg("https://customer-assets.emergentagent.com/job_c73baa61-619c-4b73-9105-ad2ee2a3c85f/artifacts/usx6igta_IMG_7883.jpg", 400, 80)}
                    alt="Ryan at the helm"
                    className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl ring-4 ring-white"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                {/* Floating "boat lights + whip cream" accent — top right */}
                <div className="absolute -top-3 -right-2 sm:-top-5 sm:-right-6 w-[4.5rem] sm:w-24 rotate-[5deg] z-10">
                  <img
                    src={proxyImg("https://customer-assets.emergentagent.com/job_c73baa61-619c-4b73-9105-ad2ee2a3c85f/artifacts/z12c9tfo_Captain%20Ryan.remini-enhanced%20%281%29.JPG", 400, 80)}
                    alt="Ryan smiling on the boat"
                    className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl ring-4 ring-white"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                {/* Floating "with friends at the marina" accent — bottom left */}
                <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-7 w-[4.5rem] sm:w-24 -rotate-[4deg] z-10">
                  <img
                    src={proxyImg("https://customer-assets.emergentagent.com/job_ecb6ab83-c1ee-4fc8-adc6-4a38fe252ef4/artifacts/lqhbn3a1_IMG_2830%202.JPG", 460, 80)}
                    alt="Ryan with friends at the marina"
                    className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl ring-4 ring-white"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                {/* Floating cert badge */}
                <div className="absolute -bottom-4 -right-2 sm:-bottom-5 sm:-right-3 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 px-2.5 py-1.5 sm:px-4 sm:py-3 flex items-center gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Licensed Captain</div>
                    <div className="text-[11px] sm:text-sm font-semibold text-gray-900">USCG · RYA · MCA · IYT · ASA</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Copy */}
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                Built by an operator
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5">
                Built by a captain.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Reviewed by operators worldwide.
                </span>
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7">
                BookingFlows isn't another generic SaaS. It's built by a licensed charter captain
                with international qualifications — and shaped by operators in Florida, the
                Mediterranean, the UAE and the Caribbean. Every feature solves a real problem
                someone with calloused hands has actually had.
              </p>

              {/* Cert pills */}
              <div className="flex flex-wrap gap-2 mb-7">
                {['USCG', 'RYA', 'MCA', 'IYT', 'ASA'].map((cert) => (
                  <span
                    key={cert}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs font-semibold text-gray-700 shadow-sm"
                  >
                    <Shield className="w-3 h-3 text-blue-600" />
                    {cert}
                  </span>
                ))}
              </div>

              {/* Stat row */}
              <div className="grid grid-cols-3 gap-3 sm:gap-5">
                <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-gray-100 p-4">
                  <div className="text-2xl font-bold text-gray-900">5+</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">Charter Regions</div>
                </div>
                <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-gray-100 p-4">
                  <div className="text-2xl font-bold text-gray-900">Lab</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">SE Background</div>
                </div>
                <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-gray-100 p-4">
                  <div className="text-2xl font-bold text-gray-900">100%</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">Operator-Built</div>
                </div>
              </div>

              {/* Meet the team link */}
              <button
                onClick={() => navigate('/meet-the-team')}
                className="group mt-7 inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                data-testid="founder-meet-team-link"
              >
                Meet the team
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>

            </div>
          </div>
        </div>
      </section>




      {/* GetMyBoat sync — concise, hands-on "manual" concierge framing (intentionally
          understated; do NOT describe any automation). */}
      <section className="py-24 bg-white" data-testid="gmb-sync-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-gray-100 bg-gray-50/60 px-6 py-8 sm:px-10 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
            <img src="/images/integrations/getmyboat-logo.png" alt="GetMyBoat" className="h-14 w-14 rounded-xl shadow-sm shrink-0" />
            <div className="flex-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">Concierge add-on</span>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">Already on GetMyBoat? One calendar.</h2>
              <p className="mt-2 text-gray-600">
                Our team <span className="font-semibold text-gray-900">manually monitors</span> your GetMyBoat bookings and keeps them in sync with BookingFlows — no double-bookings, no juggling tabs.
              </p>
            </div>
            <button
              onClick={() => { trackSignupStarted('supplier_gmb_section'); navigate('/signup'); }}
              data-testid="supplier-gmb-signup"
              className="inline-flex items-center rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 transition-colors shrink-0">
              Get started
            </button>
          </div>
        </div>
      </section>

      {/* Concrete feature grid — replaces abstract "does more" cards with
          specific, sales-ready features operators actually compare on. */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Everything operators need
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              The features your competitors{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">charge extra for</span>
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              All included. No upsells. No "Pro plan" upgrade prompts.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              // ── Revenue tools ────────────────────────────────────────────
              {
                icon: <BadgeDollarSign className="w-6 h-6" />,
                title: 'Gift Cards',
                desc: 'Depleting-balance gift cards with partial Stripe charges. Buyers and recipients get branded emails. Sell year-round, redeem instantly.',
                tint: 'from-violet-500 to-purple-600',
              },
              {
                icon: <TrendingUp className="w-6 h-6" />,
                title: 'Dynamic Pricing',
                desc: 'Surge weekends, discount slow days, tier by season — automatically. Pull more revenue from the same calendar.',
                tint: 'from-blue-500 to-cyan-600',
              },
              {
                icon: <Percent className="w-6 h-6" />,
                title: 'Discount Codes',
                desc: 'Reusable percentage or fixed-amount codes. Cap usage, set expiry, target specific services. Track redemption in real time.',
                tint: 'from-rose-500 to-pink-600',
              },
              // ── Financial control ────────────────────────────────────────
              {
                icon: <SlidersHorizontal className="w-6 h-6" />,
                title: 'Flexible Fee Control',
                desc: 'You decide who pays the fees. Operator absorbs, customer absorbs, or split. Add your own service fee to every booking with a toggle.',
                tint: 'from-emerald-500 to-teal-600',
                badge: 'New',
              },
              {
                icon: <Globe className="w-6 h-6" />,
                title: 'Multi-Currency',
                desc: 'USD, AED, EUR, GBP & more. Operators in the US, Med, UAE and beyond — each in their own currency and timezone.',
                tint: 'from-amber-500 to-orange-600',
              },
              {
                icon: <BarChart3 className="w-6 h-6" />,
                title: 'Per-Tenant Analytics',
                desc: 'GA4, Google Ads & Meta Pixel events fire under your own tenant IDs. Scale ad spend with confidence, not guesswork.',
                tint: 'from-cyan-500 to-sky-600',
              },
              // ── Operations ───────────────────────────────────────────────
              {
                icon: <Users className="w-6 h-6" />,
                title: 'Assignment on Confirmation',
                desc: 'Captains and guides claim trips from a shared dispatch queue. No more group-chat assignment chaos.',
                tint: 'from-indigo-500 to-blue-600',
              },
              {
                icon: <Zap className="w-6 h-6" />,
                title: 'No-Code Service Editor',
                desc: 'Spin up a new tour, edit pricing, or change a capacity in under a minute. Drag, drop, save — live for customers.',
                tint: 'from-pink-500 to-rose-600',
              },
              {
                icon: <Download className="w-6 h-6" />,
                title: 'Native iOS & Android Apps',
                desc: 'Your team books, dispatches and checks guests in from real native apps — not a wrapped browser tab.',
                tint: 'from-blue-600 to-indigo-700',
              },
              // ── Trust & growth ───────────────────────────────────────────
              {
                icon: <FileText className="w-6 h-6" />,
                title: 'Digital Waivers',
                desc: 'PIN-protected, device-fingerprinted, multi-signer ready. Legal audit trail captured automatically.',
                tint: 'from-slate-600 to-gray-700',
              },
              {
                icon: <Mail className="w-6 h-6" />,
                title: 'Automated Customer Comms',
                desc: 'SMS reminders, abandoned-cart recovery, thank-you emails, review nudges — sent on your behalf.',
                tint: 'from-sky-500 to-blue-600',
              },
              {
                icon: <Code className="w-6 h-6" />,
                title: 'Custom Websites Built For You',
                desc: 'Need more than a widget? We build a competitive, SEO-primed website tailored to your business — no templates.',
                tint: 'from-fuchsia-500 to-pink-600',
                badge: 'New',
              },
            ].map((f, i) => (
              <div
                key={i}
                className="group relative rounded-2xl border border-gray-100 bg-white p-6 hover:border-gray-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                {f.badge && (
                  <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    {f.badge}
                  </span>
                )}
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.tint} text-white flex items-center justify-center mb-4 shadow-sm`}>
                  {f.icon}
                </div>
                <h4 className="text-base font-semibold text-gray-900 mb-1.5 tracking-tight">{f.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Honest closer — operator-to-operator nod */}
          <div className="text-center mt-12">
            <p className="text-sm text-gray-400 italic">
              ...and a few hundred details we'll never put on a marketing page.
            </p>
          </div>
        </div>
      </section>



      {/* ───────────────────────────────────────────────────────────────
          Revenue Insights — truthful, punchy "know your best performers".
          Backed by the real per-asset revenue already shown on the Assets
          page (no "AI/algorithm" overclaim — just clear reporting).
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50" data-testid="supplier-revenue-insights">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto animate-on-scroll">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <BarChart3 className="w-3.5 h-3.5" />
              Revenue Insights
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              Know which assets{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">actually pay the bills.</span>
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              See roughly how much revenue each boat, jet ski or vehicle has pulled in over the
              last 12 months — and spot the ones quietly costing you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Bullets */}
            <div className="space-y-7 animate-on-scroll">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Revenue per asset, at a glance</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Every boat, jet ski or vehicle shows its own trailing 12-month revenue —
                    pulled straight from your real bookings, not guesswork.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Spot your best — and your laggards</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Instantly see which assets are carrying the fleet and which are barely
                    moving — so you know where to add capacity or cut your losses.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <SlidersHorizontal className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">One toggle, no spreadsheets</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Flip "Showing revenue" on or off right from your Assets page. No exports,
                    no pivot tables, no end-of-month scramble.
                  </p>
                </div>
              </div>
            </div>

            {/* Mockup — sleek per-asset revenue panel (illustrative demo data) */}
            <div className="relative animate-on-scroll">
              {/* ambient glow for depth */}
              <div className="absolute -inset-5 -z-10 bg-gradient-to-tr from-blue-300/25 via-indigo-300/10 to-transparent blur-3xl rounded-[2.5rem]" aria-hidden="true" />

              <div className="rounded-[1.75rem] border border-gray-100 bg-white/90 backdrop-blur-xl shadow-[0_30px_70px_-30px_rgba(37,99,235,0.35)] overflow-hidden">
                {/* header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100/80">
                  <div>
                    <h5 className="font-bold text-gray-900 text-[15px] leading-none tracking-tight">Assets</h5>
                    <p className="text-[11px] text-gray-400 mt-1.5">Ranked by last 12 months</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1.5 rounded-full">
                    <DollarSign className="w-3 h-3" /> Showing revenue
                  </span>
                </div>

                {/* rows */}
                <div className="p-3 sm:p-4 space-y-1.5">
                  {[
                    { name: 'Sunset Cruiser', cap: 14, rev: '$38,420', bar: 100, rank: 1, tint: 'from-blue-500 to-indigo-600', top: true },
                    { name: 'Harbor Pontoon', cap: 12, rev: '$24,180', bar: 63,  rank: 2, tint: 'from-violet-500 to-purple-600' },
                    { name: 'Wave Runner',    cap: 4,  rev: '$3,960',  bar: 10,  rank: 3, tint: 'from-slate-400 to-slate-500', low: true },
                  ].map((a) => (
                    <div
                      key={a.name}
                      className={`group relative flex items-center gap-3.5 rounded-2xl px-3.5 py-3 transition-colors ${a.top ? 'bg-blue-50/70 ring-1 ring-blue-100' : 'hover:bg-gray-50'}`}
                      data-testid={`revenue-asset-${a.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {/* rank */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${a.top ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        {a.rank}
                      </div>
                      {/* icon */}
                      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${a.tint} text-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <Anchor className="w-4 h-4" />
                      </div>
                      {/* name + revenue bar */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-gray-900 text-sm truncate">{a.name}</span>
                          <span className={`font-bold text-sm tabular-nums flex-shrink-0 ${a.low ? 'text-gray-400' : 'text-gray-900'}`}>{a.rev}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <div className="h-1.5 flex-1 rounded-full bg-gray-100 overflow-hidden">
                            <div className={`h-full rounded-full bg-gradient-to-r ${a.tint}`} style={{ width: `${a.bar}%` }} />
                          </div>
                          <span className="text-[10px] text-gray-400 flex items-center gap-0.5 flex-shrink-0">
                            <Users className="w-2.5 h-2.5" /> {a.cap}
                          </span>
                        </div>
                      </div>

                      {a.top && (
                        <span className="absolute -top-2 right-3 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-600 text-white shadow-sm">
                          Top earner
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* footer */}
                <div className="px-6 py-3 border-t border-gray-100/80 flex items-center justify-between">
                  <span className="text-[11px] text-gray-400">Trailing 12-month revenue</span>
                  <span className="text-[11px] font-medium text-amber-600 flex items-center gap-1">
                    <Activity className="w-3 h-3" /> Wave Runner is underperforming
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Waiver Management — converted from dark to light to break the
          dark-stack rhythm. The Shareable Customer Tickets section below
          covers the customer-facing side; this is the legal/audit angle. */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Shield className="w-3.5 h-3.5" />
              Legal Protection
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              Waivers that{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">actually hold up.</span>
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              Device fingerprints, IP capture, and audit trails — so you have real proof if you ever need it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-7 md:order-2">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <Fingerprint className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Device Verification</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Capture device fingerprints, IP addresses, and browser info automatically when customers sign. Full audit trail for legal protection.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">PIN-Based Tickets</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Guides get a unique PIN for each trip. Customers enter the PIN to access their ticket — guides instantly know who signed.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Custom Waiver Text</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Write your own waiver text. Multi-signature support for groups. Customers sign on their phone before the trip.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative md:order-1">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100">
                  <h5 className="font-semibold text-gray-900">Booking Waiver</h5>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Verified</span>
                </div>
                <div className="space-y-3 text-sm">
                  {[
                    ['Device', 'iPhone 15 Pro'],
                    ['IP Address', '203.0.113.45'],
                    ['Location', 'Austin, TX'],
                    ['Timestamp', '11/11/25 4:32 PM'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-gray-500">{k}</span>
                      <span className="font-mono text-gray-900">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-400">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  <span>All verification data stored securely for legal compliance</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Ticket Feature */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Link2 className="w-3.5 h-3.5" />
              Customer Experience
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              One link,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">everything they need.</span>
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              Give your customers everything in one tap — no app downloads, no confusion.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Features List */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-blue-500 p-3 rounded-lg flex-shrink-0">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">All Booking Details in One Place</h4>
                  <p className="text-gray-600">
                    Meeting location, time, asset name, guide info, and emergency contacts—everything your customer needs to show up prepared.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-500 p-3 rounded-lg flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">PIN-Protected Waiver Access</h4>
                  <p className="text-gray-600">
                    Customers sign waivers digitally using a secure PIN. Your team instantly knows who signed—no paperwork, no hassle.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-purple-500 p-3 rounded-lg flex-shrink-0">
                  <ArrowRight className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">Instantly Shareable</h4>
                  <p className="text-gray-600">
                    Send via text, email, or any messaging app. Works on any device—no app required. Customers get the link, they're ready to go.
                  </p>
                </div>
              </div>
            </div>

            {/* Ticket Mockup */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border-l-4 border-blue-600 p-8 max-w-md mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Booking ID</p>
                      <p className="font-mono font-bold text-blue-600">BF-1CWQ4T</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">Confirmed</span>
                </div>

                {/* Company Name */}
                <h3 className="font-bold text-2xl text-gray-900 mb-6">Austin ATV Rentals</h3>

                {/* Booking Details */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Asset</p>
                      <p className="font-semibold text-gray-900">Sunset Cruiser</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guide</p>
                      <p className="font-semibold text-gray-900">John S.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                      <p className="font-semibold text-gray-900">Sat, Nov 15</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                      <p className="font-semibold text-gray-900">2:00 PM</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                      <p className="font-semibold text-gray-900">3 hours</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                      <p className="font-semibold text-gray-900">8 people</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Meeting Location</p>
                    <p className="font-semibold text-gray-900">Trailhead Parking</p>
                  </div>
                </div>

                {/* Emergency */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-orange-900 mb-1">Emergency Contact</p>
                  <p className="text-sm font-mono text-orange-700">+1 (512) 555-ASSET</p>
                </div>

                {/* Who's Signed */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-green-900 mb-2 uppercase tracking-wide">Who's Signed the Waiver</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">John Smith</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">Sarah Johnson</span>
                    </div>
                    <div className="flex items-center gap-2 opacity-50 mt-2 pt-2 border-t border-green-200">
                      <div className="w-4 h-4 border-2 border-gray-400 rounded"></div>
                      <span className="text-sm text-gray-600">6 more guests need to sign</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <button className="btn-card-primary">
                    <Lock className="w-4 h-4" />
                    <span>Sign Waiver</span>
                    <span className="absolute right-4 text-xs opacity-75">2/8</span>
                  </button>
                  <button className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 fill-white" />
                    Leave a Review
                  </button>
                </div>
                
                {/* Footer - Copy Link */}
                <div className="text-center mt-6 pt-4 border-t">
                  <button className="text-sm text-gray-500 hover:text-gray-700 font-medium flex items-center justify-center gap-2 mx-auto">
                    <Link2 className="w-4 h-4" />
                    Copy Ticket Link
                  </button>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                Works on any device
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Text Content - Centered on top */}
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Globe className="w-3.5 h-3.5" />
              Expand Your Reach
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              Get listed on our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">marketplace.</span>
            </h3>
            <p className="text-lg text-gray-500 mb-6 leading-relaxed">
              Reach thousands of travelers actively searching for experiences like yours.
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-8">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Instant visibility</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Professional profile</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">SEO-optimized</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Included free</span>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/marketplace')}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium px-6 py-3 rounded-full shadow-sm"
              data-testid="supplier-view-marketplace"
            >
              View Marketplace <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
          
          {/* Visual - Browser Window Mockup below */}
          <div className="relative max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-500">bookingflows.com</span>
                </div>
              </div>
              <div className="p-4 bg-gray-50">
                {/* Marketplace Header */}
                <div className="bg-white rounded-lg p-3 mb-3 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src="/logo-icon.png" alt="BookingFlows" className="w-6 h-6 rounded" />
                      <span className="font-semibold text-sm text-gray-900">BookingFlows</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-24 bg-gray-100 rounded-full"></div>
                    </div>
                  </div>
                </div>
                {/* Experience Cards Grid - Taller with photos */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg overflow-hidden border border-gray-100">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1664532962042-07b2f58bdd8e?w=400')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">Sunset Boat Tour</p>
                      <p className="text-xs text-gray-500">Austin, TX</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$89</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.9</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg overflow-hidden border border-gray-100">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1736147936423-787f57fbfd4c?w=400')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">ATV Adventure</p>
                      <p className="text-xs text-gray-500">Hill Country</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$120</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.8</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg overflow-hidden border border-gray-100">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1632854269219-4ec2553b913c?w=400')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">Wine Tasting</p>
                      <p className="text-xs text-gray-500">Fredericksburg</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$75</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.7</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-3 -right-3 bg-blue-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg">
              Free Listing
            </div>
          </div>
        </div>
      </section>

      {/* Done-for-you Websites — real proof (BOATX + Banana Boat sites we built) */}
      <section className="py-24 bg-white" data-testid="done-for-you-websites">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-fuchsia-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Done-for-you websites
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              No website?{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-pink-600">We'll build you one.</span>
            </h3>
            <p className="text-lg text-gray-500 mb-6 leading-relaxed">
              SEO-competitive, fully integrated, and your services live from day one — no templates, no monthly website fees. The same sites that book real customers below were built on BookingFlows.
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
              {['SEO-optimized', 'Mobile-first', 'Bookings built in', 'Your brand, your domain'].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-fuchsia-600 flex-shrink-0" />
                  <span className="text-gray-700">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              { name: 'BOATX', domain: 'boatx.io', href: '/customers/boatx',
                src: 'https://1iynnenkjdavmviy.public.blob.vercel-storage.com/boatx_screen_1.jpg' },
              { name: 'Banana Boat Rentals', domain: 'bananaboatrentals.com', href: '/customers/banana-boat-rentals',
                src: 'https://customer-assets.emergentagent.com/job_d269599a-400d-440c-92a3-9218d3ccd3af/artifacts/1tapvxni_Screenshot%202026-06-12%20at%209.40.35%E2%80%AFPM.png' },
            ].map((site) => (
              <div key={site.name} className="group bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
                <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 flex items-center justify-center gap-1.5 text-xs text-gray-400">
                    <Lock className="w-3 h-3" /> {site.domain}
                  </div>
                </div>
                <div className="relative h-64 overflow-hidden bg-gray-50">
                  <img
                    src={proxyImg(site.src, 1000, 80)}
                    alt={`${site.name} website built by BookingFlows`}
                    loading="lazy"
                    decoding="async"
                    className="w-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
                <div className="flex items-center justify-between px-5 py-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{site.name}</div>
                    <div className="text-xs text-gray-400">Live on {site.domain}</div>
                  </div>
                  <button
                    onClick={() => navigate(site.href)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-fuchsia-600 hover:text-fuchsia-700 transition-colors"
                    data-testid={`dfy-story-${site.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    See the story <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/demo')}
              variant="outline"
              className="rounded-full border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-2.5"
              data-testid="dfy-book-demo"
            >
              Talk to us about a website <ArrowRight className="ml-2 w-4 h-4 inline" />
            </Button>
          </div>
        </div>
      </section>

      {/* Embeddable Booking Widget Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Text Content - Centered on top */}
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Code className="w-3.5 h-3.5" />
              Your Website
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              Embed bookings on{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">your own site.</span>
            </h3>
            <p className="text-lg text-gray-500 mb-6 leading-relaxed">
              Customers book directly from your website. No redirects. No friction.
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-8">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Copy & paste install</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Brand colors</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Mobile-responsive</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Real-time sync</span>
              </div>
            </div>
            <div className="bg-gray-900 rounded-xl p-4 font-mono text-sm inline-block">
              <p className="text-gray-400 mb-1">{`<!-- Add to your website -->`}</p>
              <p className="text-green-400">{`<script src="bookingflows.com/widget.js"></script>`}</p>
            </div>
          </div>
          
          {/* Two Widget Mockups Side by Side */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Services Grid View - with browser frame */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
              {/* Browser bar */}
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-500">yoursite.com/book</span>
                </div>
              </div>
              <div className="p-6 bg-white">
                {/* Header like real widget */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Your Company</h3>
                  <p className="text-sm text-gray-500">Choose your experience</p>
                </div>
                
                {/* Service Cards Grid - Like real widget with photos */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Service Card 1 - Sunset Cruise */}
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/5] cursor-pointer shadow-md">
                    <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1669430299454-2677085fa0a3?w=400')"}}></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <div className="flex items-baseline gap-1 mb-1 flex-wrap">
                        <span className="text-sm font-bold leading-tight">Sunset Sail Cruise</span>
                        <span className="border border-white/80 px-1.5 py-0.5 rounded text-xs font-semibold bg-black/30">$125</span>
                      </div>
                      <p className="text-[10px] opacity-80 mb-2">2 hours • Capacity: 12</p>
                      <button className="bg-blue-500 text-white text-xs font-semibold py-1.5 rounded-lg w-full">
                        Select date
                      </button>
                    </div>
                  </div>
                  
                  {/* Service Card 2 - Fishing Charter */}
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/5] cursor-pointer shadow-md">
                    <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1625183656263-171183307b15?w=400')"}}></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <div className="flex items-baseline gap-1 mb-1 flex-wrap">
                        <span className="text-sm font-bold leading-tight">Deep Sea Fishing</span>
                        <span className="border border-white/80 px-1.5 py-0.5 rounded text-xs font-semibold bg-black/30">$395</span>
                      </div>
                      <p className="text-[10px] opacity-80 mb-2">6 hours • Capacity: 8</p>
                      <button className="bg-blue-500 text-white text-xs font-semibold py-1.5 rounded-lg w-full">
                        Select date
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Calendar View - with browser frame */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
              {/* Browser bar */}
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-500">yoursite.com/book</span>
                </div>
              </div>
              <div className="p-6 bg-white">
                {/* Header */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">Book Your Experience</h3>
                
                {/* Select Date Row */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-gray-700">Select Date</span>
                    <span className="text-red-500">*</span>
                  </div>
                  <div className="flex items-center gap-1 text-green-600 text-sm">
                    <Check className="w-4 h-4" />
                    <span>Availability checked</span>
                  </div>
                </div>
                
                {/* Month Header */}
                <div className="flex items-center justify-between mb-4">
                  <ArrowRight className="w-4 h-4 text-gray-400 rotate-180" />
                  <span className="font-semibold text-gray-900">January 2026</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
                
                {/* Calendar Grid */}
                <div className="bg-gray-50 rounded-xl p-3">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2 text-center">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                      <span key={day} className="text-xs font-medium text-gray-500 py-1">{day}</span>
                    ))}
                  </div>
                  
                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty slots for alignment */}
                    {[...Array(3)].map((_, i) => (
                      <div key={`empty-${i}`} className="aspect-square"></div>
                    ))}
                    {/* Days 1-31 */}
                    {[...Array(31)].map((_, i) => {
                      const day = i + 1;
                      const isAvailable = day >= 21;
                      const isToday = day === 21;
                      return (
                        <div
                          key={day}
                          className={`aspect-square rounded-lg flex items-center justify-center text-xs font-medium ${
                            isAvailable
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-400'
                          } ${isToday ? 'ring-2 ring-blue-300' : ''}`}
                        >
                          {day}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CRM Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Users className="w-3.5 h-3.5" />
                Customer Management
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
                Every booking{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">builds your CRM.</span>
              </h3>
              <p className="text-lg text-gray-500 mb-6 leading-relaxed">
                Booking history, contact details, preferences — all collected automatically.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Complete booking history per customer</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Contact details & preferences</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Automated thank you emails</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Export data anytime</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-gray-900">Recent Customers</h4>
                  <span className="text-sm text-blue-600 font-medium">View All</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">JS</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">John Smith</p>
                      <p className="text-sm text-gray-500">3 bookings • Last: Nov 15</p>
                    </div>
                    <span className="text-green-600 font-semibold">$450</span>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 font-semibold text-sm">SJ</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Sarah Johnson</p>
                      <p className="text-sm text-gray-500">5 bookings • Last: Nov 12</p>
                    </div>
                    <span className="text-green-600 font-semibold">$825</span>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-semibold text-sm">MD</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Mike Davis</p>
                      <p className="text-sm text-gray-500">1 booking • Last: Nov 10</p>
                    </div>
                    <span className="text-green-600 font-semibold">$150</span>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Total Customers</span>
                    <span className="font-semibold text-gray-900">1,234</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QR Scanner Section */}
      <section className="py-24 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <div className="flex gap-6 justify-center items-start">
                {/* Ticket with QR Code */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 w-48 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                    <div className="bg-blue-600 p-1.5 rounded">
                      <Calendar className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Booking ID</p>
                      <p className="text-xs font-bold text-blue-600">BF-ABC123</p>
                    </div>
                  </div>
                  <p className="font-bold text-sm text-gray-900 mb-1">BOATX</p>
                  <p className="text-xs text-gray-500 mb-3">Sat, Nov 29 • 2:00 PM</p>
                  {/* QR Code */}
                  <div className="bg-gray-50 p-3 rounded-xl mb-2">
                    <QrCode className="w-full h-auto text-gray-900" />
                  </div>
                  <p className="text-[10px] text-center text-gray-400">Scan to check in</p>
                </div>

                {/* Phone Scanner */}
                <div className="relative max-w-[200px]">
                  <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl">
                    <div className="bg-white rounded-[1.5rem] overflow-hidden">
                      <div className="bg-purple-600 px-4 py-2">
                        <p className="text-white font-semibold text-sm text-center">Scan QR Code</p>
                      </div>
                      <div className="p-3">
                        {/* Scanner Viewfinder with QR */}
                        <div className="relative aspect-square bg-gray-900 rounded-xl mb-3 overflow-hidden">
                          {/* Corner brackets */}
                          <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-purple-400"></div>
                          <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-purple-400"></div>
                          <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-purple-400"></div>
                          <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-purple-400"></div>
                          {/* Scan line */}
                          <div className="absolute left-2 right-2 h-0.5 bg-purple-500 top-1/3 animate-pulse shadow-[0_0_10px_rgba(168,85,247,0.8)]"></div>
                          {/* QR Code being scanned */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 bg-white p-1.5 rounded shadow-lg">
                              <QrCode className="w-full h-full text-gray-900" />
                            </div>
                          </div>
                        </div>
                        {/* Success State */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                          <Check className="w-5 h-5 text-green-600 mx-auto mb-1" />
                          <p className="font-semibold text-green-800 text-xs">Verified!</p>
                          <p className="text-[10px] text-green-600">John Smith • 4 guests</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <QrCode className="w-3.5 h-3.5" />
                QR Check-In
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
                Turn any device{' '}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">into a scanner.</span>
              </h3>
              <p className="text-gray-600 text-lg mb-6">
                No special hardware needed. Your phone becomes a professional check-in system. Scan customer QR codes and verify bookings instantly.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Works on any smartphone or tablet</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Instant guest verification</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">See booking details immediately</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Track check-ins in real-time</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* White Label Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                Your Brand
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
                White-label it{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">as your own.</span>
              </h3>
              <p className="text-gray-600 text-lg mb-6">
                Make it yours. Remove our branding completely and present the booking system as your own. Your customers see your brand, not ours.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">Your logo on all customer touchpoints</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">Custom colors to match your brand</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">No "Powered by" watermarks</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">100% free—included with your account</span>
                </li>
              </ul>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-green-800 font-medium">
                  Free of charge. On us. No hidden fees.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="space-y-4">
                {/* Before */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-2 font-medium">BEFORE</p>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-lg"></div>
                      <span className="font-semibold text-gray-400">BookingFlows</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                    <div className="mt-3 pt-3 border-t text-xs text-gray-400 text-center">
                      Powered by BookingFlows
                    </div>
                  </div>
                </div>
                {/* After */}
                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                  <p className="text-xs text-blue-600 mb-2 font-medium">AFTER (WHITE LABEL)</p>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">YB</div>
                      <span className="font-semibold text-gray-900">Your Brand</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                    <div className="mt-3 pt-3 border-t text-xs text-gray-900 text-center font-medium">
                      © Your Company Name
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payments & Global - Combined Stripe + Multi-Currency */}
      <section className="py-24 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-blue-300 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
              <DollarSign className="w-3.5 h-3.5" />
              Payments &amp; Payouts
            </div>
            <div className="flex items-center justify-center mb-6">
              {/* Stripe Logo */}
              <div className="bg-white rounded-xl p-3">
                <svg className="w-16 h-7" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M60 12.8c0-4.28-2.07-7.66-6.03-7.66-3.98 0-6.38 3.38-6.38 7.63 0 5.04 2.84 7.58 6.92 7.58 1.99 0 3.49-.45 4.63-1.09v-3.33c-1.14.57-2.45.93-4.11.93-1.63 0-3.07-.57-3.25-2.56h8.19c0-.22.03-1.08.03-1.5zm-8.19-1.59c0-1.88 1.15-2.67 2.19-2.67 1.02 0 2.1.79 2.1 2.67h-4.29zM41.3 5.14c-1.64 0-2.7.77-3.28 1.3l-.22-1.04H34v19.8l4.22-.9v-4.8c.6.44 1.49 1.06 2.97 1.06 3 0 5.74-2.42 5.74-7.77 0-4.9-2.78-7.65-5.63-7.65zm-.99 11.76c-.99 0-1.57-.36-1.98-.8v-6.3c.44-.48 1.04-.82 1.98-.82 1.52 0 2.57 1.7 2.57 3.94 0 2.3-1.03 3.98-2.57 3.98zM27.65 4.3l4.24-.9V0l-4.24.9v3.4zm0 .98h4.24v14.6h-4.24V5.28zM22.94 6.6l-.26-1.32h-3.63v14.6h4.22v-9.9c1-.3 2.68-1.06 3.2-.88V5.28c-.54-.2-2.51-.57-3.53 1.32zM13.68 1.38L9.56 2.25v13.35c0 2.47 1.85 4.28 4.3 4.28 1.36 0 2.35-.25 2.9-.55v-3.45c-.53.22-3.17 1-3.17-1.5V8.93h3.17V5.28h-3.17l.09-3.9zM4.16 9.84c0-.65.54-.9 1.42-.9 1.27 0 2.87.39 4.14 1.08V6.2c-1.39-.55-2.76-.77-4.14-.77C2.25 5.43 0 7.1 0 10.02c0 4.5 6.2 3.78 6.2 5.72 0 .77-.67 1.02-1.6 1.02-1.39 0-3.17-.57-4.58-1.35v3.82c1.56.67 3.14.96 4.58.96 3.35 0 5.58-1.66 5.58-4.72 0-4.86-6.22-4-6.22-5.63z" fill="#635BFF"/>
                </svg>
              </div>
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 leading-[1.1]">
              Payments powered by Stripe
            </h3>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Secure payments, instant payouts, and multi-currency support—all handled by one of the most trusted financial platforms in the world.
            </p>
          </div>
          
          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-green-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <Shield className="w-5 h-5 text-green-400" />
              </div>
              <h4 className="font-semibold mb-1">Secure Payments</h4>
              <p className="text-sm text-white/70">Bank-level encryption for every transaction</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-blue-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <Zap className="w-5 h-5 text-blue-400" />
              </div>
              <h4 className="font-semibold mb-1">Instant Payouts</h4>
              <p className="text-sm text-white/70">Money in your account within days</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-purple-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <Globe className="w-5 h-5 text-purple-400" />
              </div>
              <h4 className="font-semibold mb-1">Multi-Currency</h4>
              <p className="text-sm text-white/70">Offer customers multiple currency options</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-orange-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <FileText className="w-5 h-5 text-orange-400" />
              </div>
              <h4 className="font-semibold mb-1">Tax Forms (1099)</h4>
              <p className="text-sm text-white/70">Automated tax reporting handled for you</p>
            </div>
          </div>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 text-center text-sm text-white/60">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>PCI Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>256-bit SSL</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Millions of Businesses Trust Stripe</span>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section - More Software-y */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Link2 className="w-3.5 h-3.5" />
              Integrations
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              We integrate with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">your channels.</span>
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              Sync your availability and bookings across platforms automatically.
            </p>
          </div>

          {/* Integration Visual */}
          <div className="max-w-4xl mx-auto">
            <div className="relative flex items-center justify-center gap-2 sm:gap-4 md:gap-8">
              {/* Left Platform - GetYourGuide */}
              <div className="flex flex-col items-center min-w-0">
                <div className="bg-white rounded-2xl p-2.5 sm:p-4 border-2 border-green-300 shadow-lg hover:shadow-xl transition-all relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full">LIVE</div>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl overflow-hidden">
                    <img src="/images/integrations/gyg-icon.jpg" alt="GetYourGuide" className="w-full h-full object-cover" />
                  </div>
                </div>
                <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700">GetYourGuide</span>
              </div>

              {/* Connection Line with Arrow */}
              <div className="flex items-center flex-shrink-0">
                <div className="w-3 sm:w-6 md:w-12 h-0.5 bg-gradient-to-r from-green-400 to-blue-400"></div>
                <div className="bg-blue-500 rounded-full p-1">
                  <ArrowRight className="w-3 h-3 text-white" />
                </div>
              </div>

              {/* Center - BookingFlows */}
              <div className="flex flex-col items-center min-w-0">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-2.5 sm:p-4 shadow-xl border-2 border-blue-400 relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full">SYNC</div>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                    <img src="/logo-icon.png" alt="BookingFlows" className="w-9 h-9 sm:w-12 sm:h-12 md:w-16 md:h-16 object-contain" />
                  </div>
                </div>
                <span className="mt-2 text-xs sm:text-sm font-bold text-blue-600">BookingFlows</span>
              </div>

              {/* Connection Line with Arrow */}
              <div className="flex items-center flex-shrink-0">
                <div className="bg-blue-500 rounded-full p-1">
                  <ArrowRight className="w-3 h-3 text-white rotate-180" />
                </div>
                <div className="w-3 sm:w-6 md:w-12 h-0.5 bg-gradient-to-l from-green-400 to-blue-400"></div>
              </div>

              {/* Right Platform - LetsBatch */}
              <div className="flex flex-col items-center min-w-0">
                <div className="bg-white rounded-2xl p-2.5 sm:p-4 border-2 border-green-300 shadow-lg hover:shadow-xl transition-all relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full">LIVE</div>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-black p-1 flex items-center justify-center">
                    <img src="/images/integrations/letsbatch-icon.png" alt="LetsBatch" className="w-full h-full object-contain" />
                  </div>
                </div>
                <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700">LetsBatch</span>
              </div>
            </div>

            {/* Coming Soon - Viator */}
            <div className="flex justify-center mt-8">
              <div className="flex items-center gap-3 bg-white border border-dashed border-gray-300 rounded-full px-5 py-2.5">
                <div className="w-8 h-8 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                  <img src="/images/integrations/viator-icon.png" alt="Viator" className="w-full h-full object-contain" />
                </div>
                <span className="text-sm text-gray-600">Viator</span>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Coming Soon</span>
              </div>
            </div>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <div className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">Real-time availability sync</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">Automatic booking updates</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">No double bookings</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* API — compact callout. Operators aren't dev buyers, but devs evaluating
          on their behalf should see this exists. */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center flex-shrink-0">
              <Code className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-1">Developer-friendly REST API</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Have a tech team or custom workflow? Full REST API with rate-limiting, usage analytics, and clean JSON responses. <span className="text-gray-700">Most operators never touch it — but it's there if you need it.</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {['GET /bookings', 'POST /bookings', 'PUT /team'].map((endpoint) => (
                <code key={endpoint} className="text-xs font-mono px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 border border-gray-200">
                  {endpoint}
                </code>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────────────────────────────────────────────
          Operations Timeline / Audit Trail — every action recorded.
          Accountability story that flows straight into the Security section.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white animate-on-scroll" data-testid="supplier-audit-trail-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left — copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Clock className="w-3.5 h-3.5" />
                The Log book
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-6">
                Every action, recorded.
              </h3>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                A full audit trail across your whole operation — your crew knows it as the Log book. Who moved a booking, who approved a refund, who checked a guest in — timestamped, attributed, and always there when you need it.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Every edit, refund, and reassignment attributed to a team member</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Waivers, payments, and check-ins stamped the second they happen</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Accountability that protects you in disputes and chargebacks</span>
                </li>
              </ul>
            </div>

            {/* Right — live audit feed mock */}
            <div className="relative">
              <div className="absolute -top-10 -right-8 w-64 h-64 rounded-full bg-blue-100/60 blur-3xl pointer-events-none" />
              <div className="relative bg-white rounded-2xl border border-gray-200 shadow-[0_25px_60px_-25px_rgba(15,23,42,0.25)] overflow-hidden" data-testid="supplier-audit-trail-feed">
                {/* card header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-gray-50/70">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-800">Log book</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-600">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    Live
                  </span>
                </div>
                {/* entries */}
                <div className="relative px-5 py-4">
                  <div className="absolute left-[38px] top-8 bottom-8 w-px bg-gray-100" />
                  {[
                    { time: '9:42 AM', icon: Users, tint: 'bg-blue-50 text-blue-600', text: <>Ryan reassigned <span className="font-semibold text-gray-900">Booking BK-2194</span> to Captain John</> },
                    { time: '9:45 AM', icon: FileText, tint: 'bg-purple-50 text-purple-600', text: <>Customer <span className="font-semibold text-gray-900">signed waiver</span></> },
                    { time: '9:47 AM', icon: DollarSign, tint: 'bg-emerald-50 text-emerald-600', text: <>Payment <span className="font-semibold text-gray-900">captured</span></> },
                    { time: '10:01 AM', icon: Check, tint: 'bg-teal-50 text-teal-600', text: <>Booking <span className="font-semibold text-gray-900">checked in</span></> },
                    { time: '10:04 AM', icon: Bell, tint: 'bg-amber-50 text-amber-600', text: <>SMS confirmation <span className="font-semibold text-gray-900">delivered</span></> },
                    { time: '10:10 AM', icon: ArrowLeftRight, tint: 'bg-rose-50 text-rose-600', text: <>Refund <span className="font-semibold text-gray-900">approved by Manager</span></> },
                  ].map((e, i) => (
                    <div key={i} className="relative flex items-start gap-3.5 py-2.5">
                      <div className={`relative z-10 w-9 h-9 rounded-full ${e.tint} flex items-center justify-center shrink-0 ring-4 ring-white`}>
                        <e.icon className="w-4 h-4" />
                      </div>
                      <div className="pt-0.5 min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">{e.time}</div>
                        <div className="text-sm text-gray-600 leading-snug">{e.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-blue-400 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Shield className="w-3.5 h-3.5" />
              Enterprise-Grade Security
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1] mb-6">
              Your data, protected.
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Built for businesses that take privacy seriously. We're working toward the highest compliance standards.
            </p>
          </div>

          {/* Security Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Encryption */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-green-500/30 transition-all">
              <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center mb-6">
                <Lock className="w-7 h-7 text-green-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">End-to-End Encryption</h4>
              <p className="text-gray-400 leading-relaxed">
                <span className="text-green-400 font-medium">AES-256 encryption at rest</span> and <span className="text-green-400 font-medium">TLS 1.3 in transit</span>. Your customer data, payment info, and signed waivers are protected with military-grade encryption.
              </p>
            </div>

            {/* EU Hosting */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-500/30 transition-all">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-blue-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">EU Data Residency</h4>
              <p className="text-gray-400 leading-relaxed">
                Database and APIs hosted in <span className="text-blue-400 font-medium">Frankfurt, Germany</span>—one of the world's strictest data privacy jurisdictions. Your data never leaves the EU.
              </p>
            </div>

            {/* Compliance */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-purple-500/30 transition-all">
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6">
                <FileText className="w-7 h-7 text-purple-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">Compliance-Ready</h4>
              <p className="text-gray-400 leading-relaxed">
                Building toward <span className="text-purple-400 font-medium">GDPR</span>, <span className="text-purple-400 font-medium">CCPA</span>, and <span className="text-purple-400 font-medium">SOC 2</span> compliance standards. We take privacy regulations seriously.
              </p>
            </div>
          </div>

          {/* Everything-you'd-expect checklist */}
          <div className="mb-16" data-testid="supplier-security-checklist">
            <p className="text-center text-[11px] uppercase tracking-[0.2em] text-gray-500 font-semibold mb-6">
              Everything you'd expect — already on
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                'Daily encrypted backups',
                'Cloudflare edge protection',
                'Stripe PCI-compliant payments',
                'AWS object storage',
                'Role-based permissions',
                'Two-factor authentication',
                'Audit logs',
                'Automatic SSL',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-green-400" />
                  </div>
                  <span className="text-sm text-gray-200">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Reliability stat band */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 pt-10 border-t border-white/10 text-center" data-testid="supplier-reliability-stats">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">99.9%</div>
              <div className="text-sm text-gray-400">Uptime</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">1M+</div>
              <div className="text-sm text-gray-400">API requests / month</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">0</div>
              <div className="text-sm text-gray-400">Data loss — daily encrypted backups</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">Global</div>
              <div className="text-sm text-gray-400">CDN + autoscaling infrastructure</div>
            </div>
          </div>
        </div>
      </section>

      {/* Crypto Payments — light, compact card-style. Was a heavy dark section
          that double-stacked with Stripe + Security + final CTA. */}
      <section className="py-24 bg-white animate-on-scroll">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center bg-gradient-to-br from-indigo-50 via-white to-blue-50 rounded-3xl border border-gray-100 p-8 sm:p-12">
            {/* Left: Copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <Bitcoin className="w-3.5 h-3.5" />
                Crypto Payments
              </div>
              <h3 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-4 leading-[1.1]">
                Accept crypto.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Get paid in dollars.</span>
              </h3>
              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                BookingFlows supports USDC stablecoin payments via Stripe. Customers pay with crypto —
                you receive USD with zero volatility risk. Stripe handles conversion and compliance.
              </p>
              <div className="space-y-2.5">
                {[
                  'Customers pay with USDC at checkout',
                  'You receive USD — no crypto knowledge needed',
                  'Works alongside card, Apple Pay, Google Pay',
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: payment methods card */}
            <div>
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400 font-semibold mb-4">At Checkout</p>
                <div className="space-y-2.5">
                  {[
                    { label: 'Credit / Debit Card', sub: 'Visa, Mastercard, Amex' },
                    { label: 'Apple Pay / Google Pay', sub: 'One-tap mobile payment' },
                    { label: 'Crypto (USDC)', sub: 'Stablecoin · Settles as USD', badge: 'New' },
                    { label: 'Buy Now, Pay Later', sub: 'Klarna, Affirm' },
                  ].map((m, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">{m.label}</p>
                          {m.badge && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                              {m.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{m.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <BadgeDollarSign className="w-3.5 h-3.5" />
              Simple Pricing
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              We only make money{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">when you do.</span>
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              No monthly fees. No setup costs. Just a small fee added to each booking—paid by your customer, not you.
            </p>
          </div>

          <div className="max-w-sm mx-auto">
            {/* Pricing Breakdown Card */}
            <Card className="bg-white border border-gray-200 shadow-xl rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                {/* Receipt Style Breakdown */}
                <div className="px-6 py-6">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Service</span>
                      <span className="font-medium text-gray-900">$350.00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Add-ons</span>
                      <span className="font-medium text-gray-900">+$50.00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Tax</span>
                      <span className="font-medium text-gray-900">+$33.00</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-dashed border-gray-200">
                      <span className="text-gray-700">BookingFlows Fee (3%)</span>
                      <span className="font-medium text-blue-600">+$13.00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Stripe Processing*</span>
                      <span className="font-medium text-purple-600">+$13.32</span>
                    </div>
                  </div>
                  
                  {/* Total */}
                  <div className="mt-4 pt-4 border-t-2 border-gray-900">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-900 text-lg">Customer Pays</span>
                      <span className="font-bold text-gray-900 text-xl">$459.32</span>
                    </div>
                  </div>
                  
                  {/* You Get */}
                  <div className="mt-4 bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-green-800">You Get</span>
                      <span className="font-bold text-green-700 text-xl">$433.00</span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">Service + Add-ons + Tax</p>
                  </div>
                  
                  {/* Stripe footnote */}
                  <p className="text-xs text-gray-400 mt-4">
                    *Stripe charges 2.9% + 30¢ per transaction
                  </p>
                </div>
                
                {/* CTA */}
                <div className="px-6 pb-6">
                  <Button 
                    className="btn-supplier-primary w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-4 rounded-full shadow-sm"
                    onClick={() => { trackSignupStarted('supplier_pricing'); navigate('/signup'); }}
                    data-testid="supplier-pricing-cta"
                  >
                    Get Started <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              No monthly fees • No setup costs • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Data migration — switching-anxiety killer. Sits right after pricing
          where "but I already have bookings in another system" objections peak. */}
      <section className="py-24 bg-white animate-on-scroll" data-testid="supplier-migration-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
            {/* Left: Copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <ArrowLeftRight className="w-3.5 h-3.5" />
                Switching Is Easy
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5">
                Already have trips booked?{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  We'll migrate everything.
                </span>
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7">
                Existing bookings, customers, schedules, gift cards — our engineers move it
                all for you. Data migration is a standard, well-worn software process for our
                team, not a project you have to manage. You keep taking bookings the entire time.
              </p>
              <div className="space-y-3">
                {[
                  'Upcoming trips & past booking history come with you',
                  'Customers, waivers & gift card balances preserved',
                  'Handled end-to-end by our engineers — typically days, not weeks',
                  'Zero downtime — keep selling while we move you over',
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/onboarding')}
                data-testid="migration-onboarding-link"
                className="inline-flex items-center gap-1.5 mt-7 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                See the full onboarding guide <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Right: 3-step migration card */}
            <div>
              <div className="bg-gradient-to-br from-gray-50 to-blue-50/40 rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm" data-testid="supplier-migration-steps">
                <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400 font-semibold mb-5 flex items-center gap-2">
                  <Database className="w-3.5 h-3.5" /> How Migration Works
                </p>
                <div className="space-y-5">
                  {[
                    { step: '1', title: 'Share an export', desc: 'A spreadsheet or export from your current system — any format works.' },
                    { step: '2', title: 'Our engineers map & import', desc: 'Trips, customers, schedules and balances land exactly where they belong.' },
                    { step: '3', title: 'Verify together & go live', desc: 'We walk through it with you side by side. Flip the switch when it all looks right.' },
                  ].map((s, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0 shadow-sm">
                        {s.step}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">{s.title}</p>
                        <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t border-gray-200/70 flex items-center gap-2.5">
                  <Shield className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <p className="text-xs text-gray-500">Migration is included — no extra fees, no "implementation packages."</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom feature commitment — sincere, human, low-pressure. Positions
          BookingFlows as a real engineering partner rather than rigid SaaS.
          Sits right before the final sign-up CTA so the last impression is
          "they'll actually listen to me." */}
      <section className="py-24 bg-gradient-to-br from-blue-50/40 via-white to-indigo-50/30 relative overflow-hidden animate-on-scroll">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-blue-200/25 rounded-full blur-3xl -z-0" aria-hidden="true" />
        <div className="absolute bottom-0 -right-20 w-80 h-80 bg-indigo-200/25 rounded-full blur-3xl -z-0" aria-hidden="true" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
            {/* Copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <Sparkles className="w-3.5 h-3.5" />
                We Build What You Need
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5">
                Need something custom?{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Just ask.
                </span>
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7">
                Every operator runs their business a little differently — and BookingFlows
                is built by an operator who gets that. If you need a workflow, integration,
                report, or feature that doesn't exist yet, tell us. We genuinely consider
                every request and ship the ones that make your business better.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  'Real engineering team — not a ticket queue',
                  'Roadmap shaped by operator feedback, not investor decks',
                  'New feature shipped? Available to every customer, free.',
                ].map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">{line}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="mailto:hello@bookingflows.com?subject=Custom%20feature%20request"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium text-sm sm:text-base shadow-lg shadow-blue-500/20 transition-all"
                  data-testid="supplier-custom-feature-cta"
                >
                  Tell us what you need <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="mailto:hello@bookingflows.com"
                  className="text-sm text-gray-600 hover:text-gray-900 underline-offset-4 hover:underline transition-colors"
                >
                  hello@bookingflows.com
                </a>
              </div>
            </div>

            {/* Visual — friendly inbox-style mock so the message lands as
                a real conversation, not a marketing pitch. */}
            <div className="relative">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-xl shadow-blue-500/5 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/50">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  </div>
                  <span className="text-[11px] text-gray-400 ml-2 font-medium">inbox · hello@bookingflows.com</span>
                </div>
                <div className="p-5 space-y-4">
                  {/* Inbound */}
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">SK</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-gray-400 mb-1">Sarah · Charter operator, Dubai · 2 days ago</div>
                      <div className="bg-gray-50 rounded-xl rounded-tl-sm p-3 text-sm text-gray-700 leading-relaxed">
                        Any chance you could add VAT-compliant invoicing for our UAE clients? We need it on every receipt.
                      </div>
                    </div>
                  </div>
                  {/* Outbound — our reply */}
                  <div className="flex gap-3 flex-row-reverse">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
                      <img src="/logo-icon.png" alt="BookingFlows" className="w-7 h-7 rounded-lg object-cover" />
                    </div>
                    <div className="flex-1 min-w-0 text-right">
                      <div className="text-[11px] text-gray-400 mb-1">BookingFlows · just now</div>
                      <div className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-xl rounded-tr-sm p-3 text-sm leading-relaxed text-left">
                        On it. We'll have a draft for you by Friday — VAT line item on receipts + tenant-level tax config. We'll loop you in on the rollout.
                      </div>
                      <div className="flex items-center gap-1.5 justify-end mt-2 text-[11px] text-emerald-600 font-medium">
                        <Check className="w-3 h-3" strokeWidth={3} />
                        Shipped Friday
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-center text-xs text-gray-400 mt-4">
                Real conversations. Real engineering. Real responsiveness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section — operator-focused, mirrors /marketplace styling */}
      <section className="py-24 px-4 bg-gray-50 animate-on-scroll" data-testid="supplier-app-store-section">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center p-8 md:p-12">
              <div className="text-white">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-semibold tracking-wide mb-5">
                  <Download className="w-3.5 h-3.5" /> NOW ON iOS
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  Take BookingFlows with you
                </h2>
                <p className="text-blue-100 mb-6">
                  Run your boat business from your pocket. Manage bookings, check your daily schedule, and get instant alerts the moment a new booking comes in — wherever you are.
                </p>
                <div className="flex flex-wrap gap-4">
                  {/* App Store Button — real link */}
                  <a
                    href="https://apps.apple.com/us/app/bookingflows/id6757174499"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-black hover:bg-gray-900 px-5 py-3 rounded-xl transition-colors"
                    data-testid="supplier-app-store-link"
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs text-white/70">Download on the</p>
                      <p className="text-sm font-semibold">App Store</p>
                    </div>
                  </a>

                  {/* Play Store Button — coming soon */}
                  <div className="flex items-center gap-3 bg-black/40 px-5 py-3 rounded-xl cursor-not-allowed opacity-75">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs text-white/70">Coming Soon</p>
                      <p className="text-sm font-semibold">Google Play</p>
                    </div>
                  </div>
                </div>

                {/* What you can do — under app buttons */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <p className="text-sm font-medium text-white mb-4">What you can do</p>
                  <div className="flex flex-col gap-3 text-sm text-blue-100">
                    <span className="flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Manage and reassign bookings on the go
                    </span>
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      See your daily schedule at a glance
                    </span>
                    <span className="flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      Instant push alerts the moment a booking lands
                    </span>
                  </div>
                </div>
              </div>

              {/* Phone mockup */}
              <div className="hidden md:flex justify-center">
                <div className="relative">
                  <div className="w-64 h-[500px] bg-gray-900 rounded-[3rem] p-2 shadow-2xl">
                    <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden flex items-center justify-center">
                      <div className="text-center p-6">
                        <img
                          src="/logo-icon.png"
                          alt="BookingFlows"
                          className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-lg"
                        />
                        <p className="font-bold text-gray-900 text-lg">BookingFlows</p>
                        <p className="text-gray-500 text-sm mt-1">Your fleet, in your pocket</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ───────────────────────────────────────────────────────────────
          Built in the USA & UAE — dual-HQ trust section. Two flag cards
          plus a follow-the-sun support strip.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white animate-on-scroll" data-testid="supplier-usa-uae-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Globe className="w-3.5 h-3.5" />
              Two Continents. One Team.
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Proudly built in the USA &amp; UAE
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              BookingFlows is engineered by a team spanning the United States and the United Arab Emirates — built on the water, for operators on the water, in two of the world's biggest boating markets.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* USA card */}
            <div className="group relative rounded-3xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white p-8 overflow-hidden hover:shadow-xl hover:border-blue-200 transition-all duration-300" data-testid="usa-hq-card">
              <div className="flex items-start justify-between mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_a2831e1f-cd94-4900-9f8b-f2b06f8d9d25/artifacts/oxdfkldx_Flag-United-States-of-America.webp"
                  alt="Flag of the United States"
                  className="w-20 h-[54px] rounded-lg object-cover shadow-md ring-1 ring-gray-200 group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1">
                  <MapPin className="w-3 h-3" /> Austin, Texas
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">United States</h3>
              <p className="text-gray-600 leading-relaxed mb-5">
                Product, engineering, and operator success — headquartered on Lake Travis, working side-by-side with US charter fleets, party-boat operators, and tour companies every day.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-blue-600 flex-shrink-0" /> US-based payments &amp; Tap to Pay support</li>
                <li className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-blue-600 flex-shrink-0" /> Built alongside real Texas boat operators</li>
              </ul>
            </div>

            {/* UAE card */}
            <div className="group relative rounded-3xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white p-8 overflow-hidden hover:shadow-xl hover:border-emerald-200 transition-all duration-300" data-testid="uae-hq-card">
              <div className="flex items-start justify-between mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_a2831e1f-cd94-4900-9f8b-f2b06f8d9d25/artifacts/vjzngumg_Flag-United-Arab-Emirates.webp"
                  alt="Flag of the United Arab Emirates"
                  className="w-20 h-[54px] rounded-lg object-cover shadow-md ring-1 ring-gray-200 group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1">
                  <MapPin className="w-3 h-3" /> Dubai, UAE
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">United Arab Emirates</h3>
              <p className="text-gray-600 leading-relaxed mb-5">
                Serving the Gulf's fast-growing charter scene from Dubai — multi-currency, AED-ready, and tuned for the marinas, yachts, and desert-to-sea experiences of the Emirates.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> AED pricing &amp; Gulf-region operators</li>
                <li className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> WhatsApp-first customer communication</li>
              </ul>
            </div>
          </div>

          {/* Follow-the-sun strip */}
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-5 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-center sm:text-left" data-testid="follow-the-sun-strip">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <Clock className="w-5 h-5 text-blue-600" />
              Support that follows the sun
            </div>
            <p className="text-sm text-gray-600">
              With teams 9 time zones apart, someone is always awake when your customers are booking — from Austin sunsets to Dubai sunrises.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section — keyword: "booking software sign up" */}
      <section className="relative bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center px-4 relative z-10">
          <div className="inline-flex items-center gap-2 text-blue-300 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            Top-Rated Booking Software
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Sign up for booking software<br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400"> — free to start</span>
          </h2>
          <p className="text-xl text-gray-300 mb-4 max-w-2xl mx-auto leading-relaxed">
            Join hundreds of tour operators, charter companies, and rental businesses taking bookings online 24/7.
          </p>
          <p className="text-sm text-blue-300 mb-10">
            After you sign up, our team will reach out personally to help you get set up.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => { trackSignupStarted('supplier_cta_bottom'); navigate('/signup'); }}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white text-xl px-12 py-6 rounded-full font-medium shadow-2xl shadow-blue-500/30 transform hover:scale-105 transition-all duration-300 h-auto"
              data-testid="supplier-cta-bottom"
            >
              Sign Up for Booking Software <ArrowRight className="ml-3 w-6 h-6 inline" />
            </Button>
            <Button
              onClick={() => navigate('/demo')}
              variant="outline"
              className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white text-xl px-12 py-6 rounded-full font-medium h-auto backdrop-blur"
              data-testid="supplier-cta-demo"
            >
              Book a Free Demo
            </Button>
          </div>
          <p className="text-gray-500 text-sm mt-6">No credit card required · Free to start · Free data migration · Setup in 15 minutes</p>
        </div>
      </section>

      {/* Footer — full footer mirroring /marketplace for brand congruence.
          Includes social links + customer story link + marketplace cross-link. */}
      <footer className="bg-gray-900 text-gray-400 py-14 pb-[calc(3.5rem+env(safe-area-inset-bottom))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Brand blurb */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/logo-icon.png"
                  alt="BookingFlows"
                  className="w-9 h-9 rounded-xl object-cover"
                />
                <h4 className="text-white font-bold text-lg">BookingFlows</h4>
              </div>
              <p className="text-sm leading-relaxed">
                Online booking software for tours, venues, experiences, and any business that takes reservations.
              </p>
              {/* Social row — same set as /marketplace */}
              <div className="mt-5">
                <SocialLinksDark />
              </div>
            </div>

            {/* Product */}
            <div>
              <h5 className="text-white font-semibold mb-4">Product</h5>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><button onClick={() => navigate('/customers/boatx')} className="hover:text-white transition-colors text-left">BOATX story</button></li>
                <li><button onClick={() => navigate('/customers/banana-boat-rentals')} className="hover:text-white transition-colors text-left">Banana Boat Rentals story</button></li>
                <li><button onClick={() => navigate('/marketplace')} className="hover:text-white transition-colors text-left">Marketplace</button></li>
              </ul>
            </div>

            {/* Software */}
            <div>
              <h5 className="text-white font-semibold mb-4">Software</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/for-suppliers')} className="hover:text-white transition-colors text-left">Booking Software</button></li>
                <li><button onClick={() => navigate('/for-charters')} className="hover:text-white transition-colors text-left">Charter Booking</button></li>
                <li><button onClick={() => navigate('/for-tours')} className="hover:text-white transition-colors text-left">Tour Booking</button></li>
                <li><button onClick={() => navigate('/for-rentals')} className="hover:text-white transition-colors text-left">Rental Booking</button></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h5 className="text-white font-semibold mb-4">Resources</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/demo')} className="hover:text-white transition-colors text-left" data-testid="footer-demo-link">Book a Demo</button></li>
                <li><button onClick={() => navigate('/onboarding')} className="hover:text-white transition-colors text-left" data-testid="footer-onboarding-link">Onboarding Guide</button></li>
                <li><button onClick={() => navigate('/blog')} className="hover:text-white transition-colors text-left">Blog</button></li>
                <li><button onClick={() => navigate('/find-booking')} className="hover:text-white transition-colors text-left">Find My Booking</button></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors text-left">Operator Login</button></li>
                <li><button onClick={() => { trackSignupStarted('footer'); navigate('/signup'); }} className="hover:text-white transition-colors text-left">Get Started Free</button></li>
              </ul>
            </div>

            {/* Support / Legal */}
            <div>
              <h5 className="text-white font-semibold mb-4">Support</h5>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:support@bookingflows.com" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors text-left">Terms of Service</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <p>&copy; {new Date().getFullYear()} BookingFlows. All rights reserved.</p>
            <p className="text-gray-500">Built by operators · For operators</p>
          </div>
        </div>
      </footer>

      {/* Floating "Start free" CTA — appears only after the user scrolls past
          the hero. Bottom-right, mobile-safe (honors iOS bottom inset). Pure
          conversion nudge — no chat, no socials, no bot to monitor. */}
      <div
        className={`fixed right-4 sm:right-6 z-40 transition-all duration-300 ease-out pointer-events-none ${
          showFloatingCta ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button
          onClick={() => { trackSignupStarted('floating_cta'); navigate('/signup'); }}
          className="pointer-events-auto group inline-flex items-center gap-2 pl-5 pr-4 py-3 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-semibold shadow-2xl shadow-blue-500/40 hover:shadow-blue-500/50 transition-all hover:-translate-y-0.5"
          data-testid="supplier-floating-cta"
          aria-label="Start free"
        >
          <Sparkles className="w-4 h-4" />
          <span>Start free</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
};

export default SupplierLandingPage;
